import React, { useMemo, useRef, useState } from "react";
import styled from "styled-components";

import { WorkspaceWithLicense } from "@examspark/api";
import { filterMatches, reactKeyFromIDSK } from "@examspark/common";
import {
  Button,
  LargePlaceholder,
  Modal,
  ModalProps,
  SearchField,
  SubmitButtons,
  Toolbar,
  Text,
} from "@examspark/common-ui";
import {
  IconPageNextLine,
  IconPagePreviousLine,
  IconRefreshLine,
} from "@examspark/common-ui/dist/components/icons";

import { useQueryListUserWorkspaces } from "../api/operations/user";
import { useAppContext } from "../providers/AppContext";

type StyledListItemProps = {
  disabled?: boolean;
};

const StyledListItem = styled.div<StyledListItemProps>`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  cursor: pointer;
  border-top: solid 1px #eaeaea;

  &:hover {
    background-color: #f7f7f7;
  }

  &:first-child {
    border-top: none;
  }

  ${(props) => {
    if (props.disabled) {
      return `
        opacity: 0.5;
        cursor: not-allowed;
      `;
    }
  }}
`;

const StyledToolbar = styled(Toolbar)`
  padding: 1rem;
`;
const StyledBackToolbar = styled.div`
  padding: 0.8rem 1rem;
  border-top: solid 1px #ddd;
`;

const StyledList = styled.div`
  border-top: 1px solid #ddd;
`;

const StyledWorkspaceTypeBadge = styled.div`
  text-transform: lowercase;
  font-size: 1.1rem;
  color: #666;

  &::first-letter {
    text-transform: capitalize;
  }
`;

const SwitchWorkspaceModal = (
  props: ModalProps & {
    onSelect?: (selected: WorkspaceWithLicense) => void;
  },
) => {
  const [state, dispatch] = useAppContext();
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState<WorkspaceWithLicense>();
  const [selectedParentWorkspaces, setSelectedParentWorkspaces] =
    useState<WorkspaceWithLicense[]>();
  const searchFieldRef = useRef<HTMLInputElement>(null);

  const {
    data: userWorkspaces,
    isFetching,
    refetch,
  } = useQueryListUserWorkspaces({
    userId: state.user?.id,
    appId: state.app?.id,
  });

  const selectedParentWorkspace =
    selectedParentWorkspaces?.[selectedParentWorkspaces.length - 1];
  const filteredWorkspaces = useMemo(() => {
    if (userWorkspaces == null) return undefined;
    return userWorkspaces
      .filter((w) => {
        if (selectedParentWorkspace != null) {
          return w.workspace.workspaceParentId === selectedParentWorkspace.id;
        }

        const parentWorkspaceIsAvailable = !!userWorkspaces.find(
          (wp) => wp.workspace.id === w.workspace.workspaceParentId,
        );

        return !parentWorkspaceIsAvailable;
      })
      .filter((w) => filterMatches(filter, w.workspace.name, w.workspace.id));
  }, [filter, selectedParentWorkspace, userWorkspaces]);

  const limit = 50;

  const showWorkspaces = filteredWorkspaces?.slice(0, limit);
  const isTruncated =
    showWorkspaces != null &&
    filteredWorkspaces != null &&
    showWorkspaces.length < filteredWorkspaces?.length;

  return (
    <Modal
      title="Switch Workspace"
      size="medium"
      onAfterOpen={() => searchFieldRef.current?.focus()}
      buttons={
        <SubmitButtons
          onCancel={props.onRequestClose}
          submitLabel="Switch Workspace"
          isSubmitDisabled={!selected}
          onSubmit={() => {
            if (!selected) {
              alert("Please select a workspace");
              return;
            }
            console.log("state", state);
            console.log("next", selected);
            dispatch({
              type: "SELECT_WORKSPACE_WITH_LICENSE",
              data: selected,
            });
            props.onSelect?.(selected);
          }}
        />
      }
      {...props}
    >
      <StyledToolbar>
        <div style={{ flex: 1 }}>
          <SearchField
            value={filter}
            onChange={(e) => setFilter(e)}
            inputRef={searchFieldRef}
          />
        </div>
        <Button
          title="Refresh"
          icon={<IconRefreshLine />}
          onClick={() => refetch()}
          isLoading={isFetching}
        >
          Refresh
        </Button>
      </StyledToolbar>
      {selectedParentWorkspace && (
        <StyledBackToolbar>
          <Button
            title="Back"
            icon={<IconPagePreviousLine />}
            onClick={() =>
              setSelectedParentWorkspaces((prev) => prev?.slice(0, -1))
            }
            variant="plain"
          >
            {selectedParentWorkspace.workspace?.name}
          </Button>
        </StyledBackToolbar>
      )}
      <StyledList>
        {isFetching ? (
          <LargePlaceholder>Loading...</LargePlaceholder>
        ) : (
          <>
            {isTruncated && (
              <StyledListItem>
                <Text.Body>
                  Showing first {limit} workspaces. Use the search field above
                  to show more relevant results.
                </Text.Body>
              </StyledListItem>
            )}

            {showWorkspaces?.map((w) => {
              const isCurrent = state.workspaceUser?.id === w.id;
              const hasChildWorkspaces = userWorkspaces?.find(
                (_w) => _w.workspace.workspaceParentId === w.id,
              );

              return (
                <StyledListItem
                  key={reactKeyFromIDSK(w)}
                  onClick={() => {
                    if (isCurrent) return;
                    setSelected(w);
                  }}
                >
                  <div>
                    <input
                      type="radio"
                      checked={selected?.id === w.id && selected?.sk === w.sk}
                      disabled={isCurrent}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    {w.workspace.name} {isCurrent && <>(Current)</>}
                  </div>
                  {!!w.workspace.type && (
                    <div>
                      <StyledWorkspaceTypeBadge>
                        {w.workspace.type}
                      </StyledWorkspaceTypeBadge>
                    </div>
                  )}
                  {hasChildWorkspaces && (
                    <div>
                      <Button
                        title="drill down"
                        variant="plain"
                        onClick={() => {
                          setSelectedParentWorkspaces((prev) => [
                            ...(prev ?? []),
                            w,
                          ]);
                          setFilter("");
                        }}
                      >
                        <IconPageNextLine width="10" />
                      </Button>
                    </div>
                  )}
                </StyledListItem>
              );
            })}
            {!!userWorkspaces?.length && !showWorkspaces?.length && (
              <LargePlaceholder>
                No Results. Try widening your filter.
              </LargePlaceholder>
            )}
            {!userWorkspaces?.length && (
              <LargePlaceholder>No workspaces to display.</LargePlaceholder>
            )}
          </>
        )}
        {!isFetching}
      </StyledList>
    </Modal>
  );
};

export default SwitchWorkspaceModal;
